import { useTranslation } from 'react-i18next'
import { AnimatedNumber } from '../../design/AnimatedNumber'
import { SurfaceCard } from '../../design/SurfaceCard'
import { DeltaChip } from './DeltaChip'
import { momentum } from './progressMath'
import type { MyProgress } from './useMyProgress'

const MOMENTUM_ICON = { up: '🚀', down: '🌱', flat: '🎯', start: '🧗' } as const

/**
 * The one figure that answers "am I getting better": points in the window,
 * against the window before, with a verdict in plain words and the streak.
 */
export function ProgressHero({ data, periodLabel }: { data: MyProgress; periodLabel: string }) {
  const { t } = useTranslation()
  const { current, previous, streakWeeks } = data
  const mood = momentum(current.points, previous.points)

  return (
    <SurfaceCard className="relative overflow-hidden p-5">
      {/* A soft wash of the gym's colour, so the headline feels like a reward
          and not another stat tile. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[var(--gym-primary)] opacity-10 blur-2xl"
      />
      <div className="relative">
        <div className="flex items-baseline gap-2">
          <p className="text-5xl font-semibold leading-none tracking-tight">
            <AnimatedNumber value={current.points} />
          </p>
          <DeltaChip current={current.points} previous={previous.points} />
        </div>
        <p className="mt-2 text-sm text-foreground/50">
          {t('progress.pointsIn', { period: periodLabel })}
        </p>
      </div>
      <p className="relative mt-4 text-sm font-medium">
        <span aria-hidden className="mr-1.5">
          {MOMENTUM_ICON[mood]}
        </span>
        {t(`progress.momentum_${mood}`)}
      </p>
      {/* The streak rides on the footer line rather than beside the headline:
          a five-digit score and "6 weeks in a row" cannot share a phone row,
          and the chip was the half that got cut off. */}
      <div className="relative mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <p className="text-xs text-foreground/45">
          {t('progress.previousPoints', { points: previous.points, period: periodLabel })}
        </p>
        {streakWeeks > 0 && (
          <span
            data-testid="streak"
            className="rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning-700"
          >
            🔥 {t('progress.streak', { count: streakWeeks })}
          </span>
        )}
      </div>
    </SurfaceCard>
  )
}
